import { useEffect, useRef, useState } from "react";
import { Sparkles, Cpu, X } from "lucide-react";

export default function AIPromptBar({ x, y, onClose, onGenerate }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [val, setVal] = useState("");
  const drag = useRef(null);

  useEffect(() => {
    const tb = ref.current;
    if (!tb) return;
    const w = tb.offsetWidth || 520;
    const h = tb.offsetHeight || 50;
    setPos({
      left: Math.max(12, Math.min(x, window.innerWidth - w - 12)),
      top:  Math.max(12, Math.min(y, window.innerHeight - h - 12)),
    });
    setTimeout(() => tb.querySelector("input")?.focus(), 30);
  }, [x, y]);

  const startDrag = (e) => {
    e.preventDefault();
    const r = ref.current.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    ref.current.setPointerCapture?.(e.pointerId);
  };
  useEffect(() => {
    const move = (e) => {
      if (!drag.current) return;
      const r = ref.current.getBoundingClientRect();
      let left = e.clientX - drag.current.dx;
      let top = e.clientY - drag.current.dy;
      left = Math.max(8, Math.min(left, window.innerWidth - r.width - 8));
      top = Math.max(8, Math.min(top, window.innerHeight - r.height - 8));
      setPos({ left, top });
    };
    const up = () => { drag.current = null; };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    return () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-testid="md-ai-bar"
      style={{
        position: "fixed", left: pos.left, top: pos.top, zIndex: 3200,
        background: "var(--pl-surface)", border: "1px solid var(--pl-border)",
        boxShadow: "0 24px 70px rgba(0,0,0,0.42)", borderRadius: 16, padding: 8,
        display: "flex", alignItems: "center", gap: 8,
        minWidth: 480, maxWidth: "min(720px, 96vw)",
        color: "var(--pl-text)",
      }}
      className="backdrop-blur"
      onClick={(e) => e.stopPropagation()}
    >
      <span
        onPointerDown={startDrag}
        title="Drag"
        style={{
          width: 12, alignSelf: "stretch", borderRadius: 999, cursor: "grab",
          backgroundImage: "radial-gradient(circle, currentColor 1.2px, transparent 1.6px)",
          backgroundSize: "6px 6px", opacity: 0.36, flex: "0 0 12px",
        }}
      />
      <input
        data-testid="md-ai-input"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") onGenerate(val); }}
        placeholder="Describe what you want the AI to do…"
        className="flex-1 min-w-0 h-10 rounded-lg px-3 text-[13px] outline-none font-medium"
        style={{ background: "var(--pl-bg-2)", border: "1px solid var(--pl-border)", color: "var(--pl-text)" }}
      />
      <button
        data-testid="md-ai-model"
        className="h-10 px-3 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5 border border-[var(--pl-border)] bg-white/[0.03] text-[var(--pl-text)] hover:text-[var(--pl-aqua)]"
      >
        <Cpu className="h-3.5 w-3.5" /> Model
      </button>
      <button
        data-testid="md-ai-go"
        onClick={() => onGenerate(val)}
        className="h-10 px-3 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5"
        style={{ color: "#001b18", background: "linear-gradient(135deg,#27E6D1,#B9FF2F)" }}
      >
        <Sparkles className="h-3.5 w-3.5" /> Generate AI
      </button>
      <button
        data-testid="md-ai-close"
        onClick={onClose}
        className="h-10 w-10 rounded-lg grid place-items-center border border-[var(--pl-border)] bg-white/[0.03] text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)]"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
